import { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Input } from "./Input";
import { AddressFormType, PropsWithDataTest } from "../../../types";
import styled from "styled-components";
import { palette } from "../../../styles/theme";

declare global {
  interface Window {
    google: any;
  }
}

interface Props extends PropsWithDataTest {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (address: Partial<AddressFormType>) => void;
  country?: string;
  error?: string;
  label?: string;
  fullWidth?: boolean;
}

const PredictionsDropdown = styled.ul`
  position: absolute;
  width: 100%;
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${palette.grey.darkGrey};
  border-radius: 8px;
  margin: 0;
  padding: 0;
  padding-top: 4px;
  padding-bottom: 4px;
  list-style: none;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const PredictionItem = styled.li`
  padding: 8px 12px;
  cursor: pointer;
  color: #222;
  &:hover {
    background-color: #f5f5f5;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const InputGooglePlaces = ({
  value,
  onChange,
  onPlaceSelected,
  country = "CA",
  error,
  label,
  fullWidth = false,
  "data-test": dataTest,
}: Props) => {
  const { t, i18n } = useTranslation();
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [predictions, setPredictions] = useState<any[]>([]);
  const [showPredictions, setShowPredictions] = useState(false);
  const autocompleteService = useRef<any>(null);
  const placesService = useRef<any>(null);
  const sessionToken = useRef<any>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Load Google Maps script
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.onload = () => setScriptLoaded(true);
      document.head.appendChild(script);
    } else {
      setScriptLoaded(true);
    }
  }, []);

  // Handle click outside to close predictions
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setShowPredictions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Initialize services
  useEffect(() => {
    if (scriptLoaded && window.google) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement("div")
      );
      sessionToken.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [scriptLoaded]);

  const parseGooglePlace = useCallback(
    (place: any): Partial<AddressFormType> => {
      const address: Partial<AddressFormType> = {
        address: "",
        appartment: "",
        city: "",
        state: "",
        zipcode: "",
        country: country as "CA" | "US",
      };

      place.address_components.forEach((component: any) => {
        const types = component.types;
        if (types.includes("street_number")) {
          address.address = component.long_name;
        } else if (types.includes("route")) {
          address.address =
            `${address.address || ""} ${component.long_name}`.trim();
        } else if (types.includes("subpremise")) {
          address.appartment = component.long_name;
        } else if (types.includes("locality")) {
          address.city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          address.state = component.short_name;
        } else if (types.includes("postal_code")) {
          address.zipcode = component.long_name;
        }
      });

      return address;
    },
    [country]
  );

  const isValidAddress = (address: Partial<AddressFormType>) => {
    return address.address && address.city && address.country;
  };

  const handleSelectPrediction = (prediction: any) => {
    placesService.current?.getDetails(
      {
        placeId: prediction.place_id,
        fields: [
          "address_components",
          "formatted_address",
          "name",
          "plus_code",
        ],
        sessionToken: sessionToken.current,
        language: i18n.language,
      },
      (place: any, status: string) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const address = parseGooglePlace(place);
          if (isValidAddress(address)) {
            onPlaceSelected(address);
            onChange(address.address || "");
          } else {
            console.error("Invalid address structure");
            onChange(value);
          }
        }
        setShowPredictions(false);
        // Reset session token after selection
        sessionToken.current =
          new window.google.maps.places.AutocompleteSessionToken();
      }
    );
  };

  const handlePredictions = useCallback(
    (predictions: any[], status: string) => {
      if (
        status !== window.google.maps.places.PlacesServiceStatus.OK ||
        !predictions
      ) {
        setPredictions([]);
        return;
      }
      setPredictions(predictions);
      setShowPredictions(true);
    },
    []
  );

  const handleChange = (value: string) => {
    onChange(value);
    setShowPredictions(true);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (value.length > 2 && autocompleteService.current) {
        autocompleteService.current.getPlacePredictions(
          {
            input: value,
            componentRestrictions: { country },
            sessionToken: sessionToken.current,
            types: ["address"],
            language: i18n.language === "fr" ? "fr-CA" : "en",
          },
          handlePredictions
        );
      } else {
        setPredictions([]);
        setShowPredictions(false);
      }
    }, 300);
  };

  if (!scriptLoaded) {
    return (
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        label={label || t("checkout.addressLine1")}
        error={error}
        autoComplete="off"
        fullWidth={fullWidth}
        noMaxWidth
        data-test={dataTest}
      />
    );
  }

  return (
    <InputWrapper ref={wrapperRef}>
      <Input
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        label={label || t("checkout.addressLine1")}
        error={error}
        autoComplete="off"
        fullWidth={fullWidth}
        noMaxWidth
        data-test={dataTest}
      />
      {showPredictions && predictions.length > 0 && (
        <PredictionsDropdown>
          {predictions.map((prediction) => (
            <PredictionItem
              key={prediction.place_id}
              data-test={`${dataTest}.predictionItem`}
              onClick={() => handleSelectPrediction(prediction)}
            >
              {prediction.description}
            </PredictionItem>
          ))}
        </PredictionsDropdown>
      )}
    </InputWrapper>
  );
};

export default InputGooglePlaces;
